// Das Hilfe-Register (Auftrag 8, R1). Ein Begriff, ein Text -- überall
// derselbe, auf der Mieterseite, im Kontoauszug und in der Wache.
//
// DIE REGEL ÜBER ALLEM: Hilfe erklärt die Festlegung, nicht die Oberfläche.
// Nicht „hier Datum eingeben", sondern was das Datum im System bedeutet.
// Jeder Text ist ABGESCHRIEBEN aus einem Code-Kommentar, einem Auftrag oder
// einem Votum, und `quelle` nennt die Fundstelle (Pfad ab Repo-Wurzel, mit
// :zeile oder :von-bis). Ein Text ohne Quelle gehört nicht hierher: Die Hilfe
// ist genau dann schädlich, wenn sie etwas behauptet, das die App nicht tut.
// Fehlt zu einem Begriff die Quelle, wird der Eintrag nicht erfunden, sondern
// als Entscheidungsmemo vorgelegt.
//
// WAS NICHT HIERHER GEHÖRT -- die Trennung, an der alles hängt:
//   Schicht 1 (Begriff) und Schicht 4 (Seite) stehen im Register.
//   Schicht 2 (Zustand: Ampel, Status, Befund) bekommt KEINEN Eintrag. Dort
//   ist der `grund` / die `klaerung` aus der Backend-Antwort der Text. Ein
//   zweiter, danebenstehender Satz veraltet genau dann, wenn jemand bewerte()
//   ändert. Was eine SPALTE oder ÜBERSCHRIFT bedeutet, ist Schicht 1 und darf
//   hierher; was ein einzelner WERT gerade bedeutet, nie.
//
// Einträge mit id "seite.<name>" sind Seitenköpfe (<Seitenkopf id>): `text`
// ist dann {frage, tun, nicht} -- welche Frage die Seite beantwortet, was man
// hier tut, was nicht. Alle anderen sind Begriffe (<Hilfe id>) mit Text als
// Zeichenkette.
//
// Dieses Modul importiert nichts: scripts/hilfe_pruefen.mjs lädt es vor dem
// Bau unverändert in Node.

const EINTRAEGE = [
    {
        id: "mietende",
        text: "Das Mietende ist der letzte Miettag, meist ein Monatsletzter, und beendet das Soll. "
            + "Die Übergabe (Schlüsselrückgabe) ist ein zweites Datum, fällt selten auf denselben "
            + "Tag und beendet das Soll nicht.",
        mehr: "Festlegung vom 05.10.2026. Für jedes laufende Mietverhältnis braucht es das "
            + "Mietende; die Übergabe ist optional. Das System erfindet kein Datum.",
        quelle: [
            "frontend/src/pages/Tenants.jsx:530-533",
            "backend/server.py:1326-1328",
            "fahrplan/voten/2026-11-03_hilfe-systematik.md:47-48",
        ],
    },
    {
        id: "arbeitsliste",
        text: "Die Liste rechnet nichts selbst: Sie liest die Ampeln der Module, und keine Zeile "
            + "verlangt eine neue Eingabe. Leer heißt „nichts zu tun“, nicht „niemand hat "
            + "eingetragen“ – deshalb zeigt sie „nichts offen“ nur, wenn alle Quellen geantwortet haben.",
        quelle: "frontend/src/pages/Dashboard.jsx:415-423",
    },
    {
        id: "seite.durchsicht",
        text: {
            frage: "Welche vorgeschlagenen Zuordnungen gelten? Erst eine bestätigte Zuordnung zählt "
                + "gegen das Soll – der Vorschlag nimmt die Arbeit ab, nicht die Entscheidung.",
            tun: "Vorschläge bestätigen oder verwerfen. Zwei Bündelungen nehmen die Masse: "
                + "gleichförmige Jahre je Mieter (jede Periode genau das Soll, immer derselbe "
                + "Einzahler) und sichere Einzelne (Sicherheit ≥ 0,9, höchstens sieben Tage von der "
                + "Fälligkeit, nur auf Einheitenkonten).",
            nicht: "Was in keine Bündelung fällt, wird nicht pauschal entschieden: Es bleibt "
                + "Einzelfall mit seinem Grund.",
        },
        quelle: [
            "backend/mietkonto.py:1904-1907",
            "frontend/src/pages/Durchsicht.jsx:49-56",
        ],
    },
];

// Pfad ab Repo-Wurzel, optional :zeile oder :von-bis. Kein freier Satz.
const FUNDSTELLE = /^[\w.-]+(\/[\w.-]+)*\.[A-Za-z0-9]+(:\d+(-\d+)?)?$/;

export const istSeite = (id) => String(id).startsWith("seite.");

export const quellenListe = (quelle) => (Array.isArray(quelle) ? quelle : [quelle]);

/** Alle Formfehler einer Eintragsliste, als Sätze. Leer heißt: in Ordnung. */
export function registerFehler(liste) {
    const fehler = [];
    const gesehen = new Set();
    liste.forEach((e, i) => {
        const wo = `Eintrag ${i + 1}${e && e.id ? ` "${e.id}"` : ""}`;
        if (!e || typeof e.id !== "string" || !e.id.trim()) {
            fehler.push(`${wo}: id fehlt`);
            return;
        }
        if (gesehen.has(e.id)) fehler.push(`${wo}: id doppelt`);
        gesehen.add(e.id);
        if (istSeite(e.id)) {
            const t = e.text || {};
            for (const k of ["frage", "tun", "nicht"]) {
                if (typeof t[k] !== "string" || !t[k].trim()) fehler.push(`${wo}: Seitenkopf ohne „${k}“`);
            }
        } else if (typeof e.text !== "string" || !e.text.trim()) {
            fehler.push(`${wo}: text fehlt`);
        }
        const q = e.quelle == null ? [] : quellenListe(e.quelle);
        if (!q.length) fehler.push(`${wo}: quelle fehlt`);
        q.forEach((s) => {
            if (typeof s !== "string" || !FUNDSTELLE.test(s)) {
                fehler.push(`${wo}: quelle „${s}“ ist keine Fundstelle (Pfad[:zeile[-zeile]])`);
            }
        });
    });
    return fehler;
}

const produktion = () => typeof process !== "undefined" && process.env.NODE_ENV === "production";

/**
 * Prüft die Liste und gibt die gültigen Einträge zurück. Außerhalb der
 * Produktion wirft ein Fehler -- in Entwicklung und Tests soll er sofort
 * auffallen. In Produktion fällt nur der kaputte Eintrag weg; der eigentliche
 * Wächter ist ohnehin das Prüfskript vor dem Bau.
 */
export function pruefeEintraege(liste, { werfen = !produktion() } = {}) {
    const fehler = registerFehler(liste);
    if (!fehler.length) return liste;
    if (werfen) throw new Error(`Hilfe-Register fehlerhaft:\n${fehler.join("\n")}`);
    console.error(`Hilfe-Register fehlerhaft:\n${fehler.join("\n")}`);
    const ids = new Set();
    return liste.filter((e) => {
        if (registerFehler([e]).length || ids.has(e.id)) return false;
        ids.add(e.id);
        return true;
    });
}

export const EINTRAEGE_ROH = EINTRAEGE;
const GUELTIG = pruefeEintraege(EINTRAEGE);
export const REGISTER = Object.fromEntries(GUELTIG.map((e) => [e.id, e]));
export const HILFE_IDS = GUELTIG.map((e) => e.id);

/**
 * Der Eintrag zu einer id. Unbekannt: wirft außerhalb der Produktion, in
 * Produktion null plus console.error -- die Seite bleibt heil.
 */
export function eintrag(id, { werfen = !produktion() } = {}) {
    const e = REGISTER[id];
    if (e) return e;
    const meldung = `Hilfe-Register: unbekannte id „${id}“`;
    if (werfen) throw new Error(meldung);
    console.error(meldung);
    return null;
}
